import * as React from "react";
// Constants
import { PlanTaskCardPlaceHolders } from "@ms/planner-generic-controls/lib/local/constants/StyleConstants";
import { IrisUiCommand } from "@ms/uno-constants/lib/local/IrisConstants";
import { SubRouteType, ViewType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
import { GridViewName } from "@ms/uno-constants/lib/local/TaskGridConstants";
import { ViewElementIds } from "@ms/uno-constants/lib/local/UIConstants";
import { ViewName } from "@ms/uno-constants/lib/local/ViewConstants";
// Controls
import { ActionCreator, useActionCreators } from "@ms/planner-container-controls/lib/local/hooks/useActionCreators";
import { LazyPlanTasksBoardView } from "@ms/planner-container-controls/lib/local/views/board/LazyPlanTasksBoardView";
import { LazyPlanTasksCalendarView } from "@ms/planner-container-controls/lib/local/views/calendar/LazyPlanTasksCalendarView";
import { LazyPlanTasksChartsView } from "@ms/planner-container-controls/lib/local/views/charts/LazyPlanTasksChartsView";
import { LazyPlanGoalsView } from "@ms/planner-container-controls/lib/local/views/goals/LazyPlanGoalsView";
import { LazyCanvasPlanTasksGridView } from "@ms/planner-container-controls/lib/local/views/grid/LazyCanvasPlanTasksGridView";
import { LazyPlanTasksGridView } from "@ms/planner-container-controls/lib/local/views/grid/LazyPlanTasksGridView";
import { LazyPlanTeamView } from "@ms/planner-container-controls/lib/local/views/people/LazyPlanTeamView";
import { LazyReportsView } from "@ms/planner-container-controls/lib/local/views/reports/LazyReportsView";
import { LazyPlanTasksTimelineView } from "@ms/planner-container-controls/lib/local/views/timeline/LazyPlanTasksTimelineView";
import { PageHeaderLayout } from "@ms/planner-generic-controls/lib/local/pageHeaderLayout/PageHeaderLayout";
import { SuspenseSpinner } from "@ms/planner-generic-controls/lib/local/suspenseSpinner/SuspenseSpinner";
import { ViewTransformBarLayout } from "@ms/planner-generic-controls/lib/local/viewTransformBarLayout/ViewTransformBarLayout";
import { ErrorView } from "@ms/planner-presentational-controls/lib/local/errorView/ErrorView";
import { PageLayout } from "@ms/planner-presentational-controls/lib/local/pageLayout/PageLayout";
import { PlanDetailsTab } from "@ms/planner-presentational-controls/lib/local/planDetailsDialog/PlanDetailsTab";
import { PlanErrorView } from "@ms/planner-presentational-controls/lib/local/planErrorView/PlanErrorView";
import { SensitivityMessageBar } from "@ms/planner-presentational-controls/lib/local/sensitivityMessageBar/SensitivityMessageBar";
import { getAdminBlockedAccessString } from "@ms/planner-presentational-controls/lib/local/utilities/AdminBlockedAccessUtilities";
import { TaskBoardViewPlaceHolder } from "@ms/planner-presentational-controls/lib/local/uxPlaceholders/taskBoardView/TaskBoardViewPlaceHolder";
import { PlanHeaderInfoContainer } from "./header/PlanHeaderInfoContainer";
import { PlanHeaderLinksContainer } from "./header/PlanHeaderLinksContainer";
import { MeetingPlanExpirationMessageBarContainer } from "./meetingPlanExpirationMessageBar/MeetingPlanExpirationMessageBarContainer";
import { PlanCalendarNudgeContainer } from "./planCalendarNudge/PlanCalendarNudgeContainer";
import { ViewActionBarContainer } from "./viewTransform/ViewActionBarContainer";
import { ViewNavigationBarContainer } from "./viewTransform/ViewNavigationBarContainer";
// Hooks
import { useConfigProvider } from "@ms/planner-container-controls/lib/local/hooks/useConfigProvider";
import { useRenderTracker } from "@ms/planner-container-controls/lib/local/hooks/useRenderTracker";
import { useStores } from "@ms/planner-container-controls/lib/local/hooks/useStores";
// Models
import { TaskAccessContext } from "@ms/planner-models/lib/local/client/planner/Enumerations";
import { ViewContext } from "@ms/planner-models/lib/local/contexts/IViewContext";
// Stores
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
import { isStateFailure } from "@ms/planner-stores/lib/local/utilities/StatusUtilities";
// Utilities
import { shouldDisplayLoadingIndicator } from "@ms/planner-generic-controls/lib/local/utilities/loadingIndicator/LoadingIndicatorUtilities";
import { extractPlanRoutingDetailsFromSubRoute } from "@ms/uno-routing/lib/local/utilities/RouteUtilities";
import { Suspense } from "react";
import { calculatePlanPageRenderState } from "./PlanPage.perf";
// Resources
import exclamationMarkImageDark from "@ms/uno-resources/lib/local/images/exclamationMarkDark.png";
import exclamationMarkImageLight from "@ms/uno-resources/lib/local/images/exclamationMarkLight.png";
/**
 * Plan page component
 */ export const PlanPage = (props)=>{
    const { planId, isTabView, renderReportSection } = props;
    const configProvider = useConfigProvider();
    // Refs for imperative access to the active Gantt-based view.
    const timelineViewRef = React.useRef(null);
    const teamViewRef = React.useRef(null);
    // Each Gantt view owns its natural date range. The action bar reads the active range,
    // avoiding duplicate task-store calculations and stale bounds when switching pivots.
    const [timelineDateRange, setTimelineDateRange] = React.useState(null);
    const [teamDateRange, setTeamDateRange] = React.useState(null);
    const [planStore, routeStore, viewLifecycleStore, viewStore, mruStore, teamsStore, permissionsStore] = useStores([
        Store.Plan,
        Store.Route,
        Store.ViewLifecycle,
        Store.View,
        Store.Mru,
        Store.Teams,
        Store.Permissions
    ]);
    const [routeActionCreator, planActionCreator, viewTransformsActionCreator] = useActionCreators([
        ActionCreator.Routing,
        ActionCreator.Plan,
        ActionCreator.ViewTransforms
    ]);
    const [isPlanSettingsPanelOpen, setIsPlanSettingsPanelOpen] = React.useState(false);
    const [planSettingsFocusElement, setPlanSettingsFocusElement] = React.useState();
    const [planSettingsInitialTab, setPlanSettingsInitialTab] = React.useState();
    const openPlanSettingsPanel = React.useCallback((elementToFocusOnDismiss, initialTab)=>{
        setIsPlanSettingsPanelOpen(true);
        setPlanSettingsFocusElement(elementToFocusOnDismiss);
        setPlanSettingsInitialTab(initialTab);
    }, []);
    const closePlanSettingsPanel = React.useCallback(()=>{
        setIsPlanSettingsPanelOpen(false);
        setPlanSettingsFocusElement(undefined);
        setPlanSettingsInitialTab(undefined);
    }, []);
    const pendingUiCommand = viewStore.getPendingUiCommand();
    React.useEffect(()=>{
        if (pendingUiCommand === IrisUiCommand.OpenPlanScheduleSettings) {
            openPlanSettingsPanel(undefined, PlanDetailsTab.Scheduling);
            viewTransformsActionCreator.requestUiCommand(null);
        }
    }, [
        openPlanSettingsPanel,
        pendingUiCommand,
        viewTransformsActionCreator
    ]);
    // Clear an unconsumed command before this page can represent a different plan or unmounts.
    React.useEffect(()=>{
        return ()=>{
            viewTransformsActionCreator.requestUiCommand(null);
        };
    }, [
        planId,
        viewTransformsActionCreator
    ]);
    const planSettingsPanelController = React.useMemo(()=>({
            isOpen: isPlanSettingsPanelOpen,
            elementToFocusOnDismiss: planSettingsFocusElement,
            initialTab: planSettingsInitialTab,
            open: openPlanSettingsPanel,
            dismiss: closePlanSettingsPanel
        }), [
        closePlanSettingsPanel,
        isPlanSettingsPanelOpen,
        openPlanSettingsPanel,
        planSettingsFocusElement,
        planSettingsInitialTab
    ]);
    // Get current pivot
    const subRouteType = routeStore.getCurrentSubRouteType();
    const subRoute = routeStore.getCurrentSubRoute();
    const planRoutingDetails = extractPlanRoutingDetailsFromSubRoute(subRoute, subRouteType);
    const currentPivot = planRoutingDetails.viewType || ViewType.Board;
    const handleGoToDateSelect = React.useCallback((date)=>{
        if (currentPivot === ViewType.Timeline) {
            timelineViewRef.current?.scrollToDate(date);
        } else if (currentPivot === ViewType.People) {
            teamViewRef.current?.scrollToDate(date);
        }
    }, [
        currentPivot
    ]);
    const activeGanttDateRange = currentPivot === ViewType.People ? teamDateRange : timelineDateRange;
    // Get current plan
    const currentPlanStoreReadResult = planStore.getPlan(planId);
    const currentPlan = currentPlanStoreReadResult.data;
    const viewId = {
        name: ViewName.PlanPage,
        partitionKey: planId
    };
    // Hook to track the view render state of the plan page
    useRenderTracker(viewId, calculatePlanPageRenderState);
    /**
     * Render the plan view
     */ const renderView = ()=>{
        if (!currentPlan || !viewStore) {
            return /*#__PURE__*/ React.createElement(React.Fragment, null);
        }
        let planView;
        switch(currentPivot){
            case ViewType.Board:
                planView = /*#__PURE__*/ React.createElement(Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(TaskBoardViewPlaceHolder, {
                        numberOfTaskCardsPerColumn: PlanTaskCardPlaceHolders,
                        showAddTaskButtonShimmer: true
                    })
                }, /*#__PURE__*/ React.createElement(LazyPlanTasksBoardView, {
                    autoFocus: false,
                    viewId: viewId,
                    isSlimView: false
                }));
                break;
            case ViewType.Schedule:
                planView = /*#__PURE__*/ React.createElement(Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
                }, /*#__PURE__*/ React.createElement(LazyPlanTasksCalendarView, {
                    autoFocus: false,
                    viewId: viewId,
                    isSlimView: false
                }));
                break;
            case ViewType.Charts:
                planView = /*#__PURE__*/ React.createElement(Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
                }, /*#__PURE__*/ React.createElement(LazyPlanTasksChartsView, {
                    planId: planId
                }));
                break;
            case ViewType.Goals:
                planView = /*#__PURE__*/ React.createElement(Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
                }, /*#__PURE__*/ React.createElement(LazyPlanGoalsView, {
                    viewId: viewId,
                    viewName: GridViewName.GoalTasksGridView
                }));
                break;
            case ViewType.Grid:
                planView = configProvider().flights.EnableCanvasTaskGrid ? /*#__PURE__*/ React.createElement(Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
                }, /*#__PURE__*/ React.createElement(LazyCanvasPlanTasksGridView, {
                    viewId: viewId,
                    isSlimView: false,
                    configProvider: configProvider,
                    viewName: GridViewName.PlanTasksGridView
                })) : /*#__PURE__*/ React.createElement(Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
                }, /*#__PURE__*/ React.createElement(LazyPlanTasksGridView, {
                    autoFocus: false,
                    viewId: viewId,
                    isSlimView: false,
                    configProvider: configProvider,
                    viewName: GridViewName.PlanTasksGridView,
                    enableEnterFooterRowInEditRow: true
                }));
                break;
            case ViewType.Report:
                planView = /*#__PURE__*/ React.createElement(Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(SuspenseSpinner, null)
                }, /*#__PURE__*/ React.createElement(LazyReportsView, {
                    plan: currentPlan,
                    renderReportSection: renderReportSection
                }));
                break;
            case ViewType.Timeline:
                planView = /*#__PURE__*/ React.createElement(Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
                }, /*#__PURE__*/ React.createElement(LazyPlanTasksTimelineView, {
                    ref: timelineViewRef,
                    viewId: viewId,
                    autoFocus: false,
                    isSlimView: false,
                    onDateRangeChange: setTimelineDateRange
                }));
                break;
            case ViewType.People:
                planView = configProvider().flights.EnableTeamViewEvo ? /*#__PURE__*/ React.createElement(Suspense, {
                    fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
                }, /*#__PURE__*/ React.createElement(LazyPlanTeamView, {
                    ref: teamViewRef,
                    viewId: viewId,
                    autoFocus: false,
                    isSlimView: false,
                    configProvider: configProvider,
                    onDateRangeChange: setTeamDateRange
                })) : /*#__PURE__*/ React.createElement(React.Fragment, null);
                break;
            case ViewType.Assignments:
            case ViewType.CopilotHome:
            default:
                planView = /*#__PURE__*/ React.createElement(React.Fragment, null);
        }
        return /*#__PURE__*/ React.createElement(ViewContext.Provider, {
            value: viewStore.getViewContext(TaskAccessContext.Plan, planId)
        }, planView);
    };
    /**
     * Callback to navigate to My Plans
     */ const navigateToMyPlans = ()=>{
        routeActionCreator.navigate({
            subRouteType: SubRouteType.MyPlans,
            subRoute: []
        });
    };
    /**
     * Callback to retry loading the plan
     */ const retryLoadPlan = ()=>{
        window.location.reload();
    };
    /**
     * Callback to remove plan from My plans
     */ const removePlanFromMyPlans = async ()=>{
        planActionCreator.removePlanFromMyPlans(planId);
        navigateToMyPlans();
    };
    /**
     * Render the error page
     * @param status The status of the store plan entity
     */ const renderErrorPage = (state)=>{
        // Check if the plan is in the my plans list
        const teamsTasksTabsForPlan = teamsStore.getTeamsTasksTabsForPlan(planId);
        const isMyPlansPlan = mruStore.getMruItemByServerDocId(planId) != null || teamsTasksTabsForPlan != null && teamsTasksTabsForPlan.length > 0;
        return /*#__PURE__*/ React.createElement(PlanErrorView, {
            planStatus: state,
            isTeamsTabView: isTabView,
            isMyPlansPlan: isMyPlansPlan,
            navigateToMyPlansButtonCallback: navigateToMyPlans,
            removePlanFromMyPlans: removePlanFromMyPlans,
            retryCallback: retryLoadPlan
        });
    };
    /**
     * Render the sensitivity message bar if the plan has sensitivity label restrictions
     */ const renderSensitivityMessageBar = ()=>{
        const enableContentLevelSensitivityLabels = configProvider().flights.EnableContentLevelSensitivityLabelsEvo;
        if (!enableContentLevelSensitivityLabels) {
            return;
        }
        if (!currentPlan) {
            return;
        }
        // Get sensitivity-based permissions
        const sensitivityPermissions = permissionsStore.getPlanPermissionsFromUserRightsObject(currentPlan.id);
        const sensitivityUserRightsFetchStatus = permissionsStore.getPlanSensitivityUserRights(currentPlan.id).status;
        const isFetchComplete = sensitivityUserRightsFetchStatus.state !== StoreEntityState.Absent;
        // Show message bar only if fetch is complete and sensitivity label restricts editing
        if (!isFetchComplete || sensitivityPermissions.canEditAllowedBySensitivity) {
            return;
        }
        return /*#__PURE__*/ React.createElement(SensitivityMessageBar, null);
    };
    const renderPlanCalendarNudgeMessageBar = ()=>{
        if (!configProvider().flights.EnableEvoCalendar) {
            return;
        }
        return /*#__PURE__*/ React.createElement(PlanCalendarNudgeContainer, {
            planId: planId,
            onOpenPlanSettings: (elementToFocusOnDismiss)=>planSettingsPanelController.open(elementToFocusOnDismiss, PlanDetailsTab.Scheduling)
        });
    };
    const renderMeetingPlanExpirationMessageBar = ()=>{
        if (!configProvider().flights.EnableMeetingPlanExpiration) {
            return;
        }
        return /*#__PURE__*/ React.createElement(MeetingPlanExpirationMessageBarContainer, {
            planId: planId
        });
    };
    const storeState = currentPlanStoreReadResult.status.state;
    if (storeState != null && isStateFailure(storeState)) {
        return renderErrorPage(storeState);
    }
    // Tenant admin has blocked Planner access to this plan's container type (e.g. Teams channels).
    // Surface the admin-blocked error screen instead of rendering the plan.
    if (currentPlan != null && permissionsStore.getIsContainerAccessBlockedByTenantAdmin(currentPlan.container)) {
        const adminBlockedStrings = getAdminBlockedAccessString(currentPlan.container.containerType);
        return /*#__PURE__*/ React.createElement(ErrorView, {
            errorText: adminBlockedStrings.errorText,
            errorSubText: adminBlockedStrings.errorSubText,
            imageLight: exclamationMarkImageLight,
            imageDark: exclamationMarkImageDark
        });
    }
    return /*#__PURE__*/ React.createElement(PageLayout, {
        pageId: ViewElementIds.BasicPlanView,
        pageTestId: "plan-page",
        header: /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(PageHeaderLayout, {
            title: /*#__PURE__*/ React.createElement(PlanHeaderInfoContainer, {
                planId: planId,
                planSettingsPanelController: planSettingsPanelController
            }),
            actions: /*#__PURE__*/ React.createElement(PlanHeaderLinksContainer, {
                planId: planId
            }),
            isTeamsTabView: isTabView,
            showPlaceholder: currentPlan == null,
            shouldDisplayLoadingIndicator: shouldDisplayLoadingIndicator(configProvider, viewLifecycleStore.getViewDataFetchState(viewId))
        })),
        viewTransformBar: /*#__PURE__*/ React.createElement(ViewTransformBarLayout, {
            isTabView: isTabView,
            actions: /*#__PURE__*/ React.createElement(PlanHeaderLinksContainer, {
                planId: planId
            }),
            viewNavigationBar: /*#__PURE__*/ React.createElement(ViewNavigationBarContainer, {
                planId: planId
            }),
            viewActionBar: /*#__PURE__*/ React.createElement(ViewActionBarContainer, {
                planId: planId,
                onGoToDateSelect: handleGoToDateSelect,
                goToDateMinDate: activeGanttDateRange?.ganttStartDate,
                goToDateMaxDate: activeGanttDateRange?.ganttEndDate
            })
        }),
        isTeamsTabView: isTabView,
        view: renderView(),
        topMessageBar: /*#__PURE__*/ React.createElement(React.Fragment, null),
        sensitivityMessageBar: renderSensitivityMessageBar(),
        meetingPlanExpirationMessageBar: renderMeetingPlanExpirationMessageBar(),
        planCalendarNudgeMessageBar: renderPlanCalendarNudgeMessageBar(),
        aiMessageBar: /*#__PURE__*/ React.createElement(React.Fragment, null),
        backgroundUrl: currentPlan?.planView?.visuals?.background?.overlayImage || ""
    });
};
